import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { leadService } from '../../../services/leadService';
import { followUpService } from '../../../services/followUpService';
import { Spinner } from '../../../components/ui/Spinner';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { FollowUpModal } from '../../../components/app/followups/FollowUpModal';
import { AskFollowUp } from '../../../components/app/followups/AskFollowUp';
import { OpportunityWatchPanel } from '../../../components/app/followups/OpportunityWatchPanel';
import { CompleteNote } from '../../../components/app/followups/CompleteNote';
import { ConnectionDetailModal } from '../../../components/app/connections/ConnectionDetailModal';
import { isPast, isToday, startOfDay } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

export const RemindersList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [completingId, setCompletingId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [watchVersion, setWatchVersion] = useState(0);

  useEffect(() => {
    fetchReminders();
  }, [user]);

  const flash = (variant, text) => {
    setMessage({ variant, text });
    setTimeout(() => setMessage(null), 3500);
  };

  const fetchReminders = async () => {
    if (!user?.id) return;
    try {
      const [data, done] = await Promise.all([
        followUpService.listOpen(user.id),
        followUpService.listCompleted(user.id)
      ]);
      setReminders(data);
      setCompleted(done);
    } catch (err) {
      console.error('Failed to load reminders:', err);
      flash('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (reminderId, completionNote) => {
    try {
      await followUpService.complete(reminderId, user.id, completionNote);
      setCompletingId(null);
      await fetchReminders();
      flash('success', 'Follow-up completed.');
    } catch (err) {
      console.error(err);
      flash('error', err.message);
      throw err;
    }
  };

  const handleDismiss = async (reminderId) => {
    try {
      await leadService.deleteReminder(reminderId, user.id);
      setReminders(prev => prev.filter(r => r.id !== reminderId));
    } catch (err) {
      console.error(err);
      flash('error', err.message);
    }
  };

  const handleReopen = async (reminderId) => {
    try {
      await followUpService.reopen(reminderId, user.id);
      await fetchReminders();
      flash('success', 'Follow-up reopened.');
    } catch (err) {
      flash('error', err.message);
    }
  };

  const handleDeleteCompleted = async (reminderId) => {
    if (!window.confirm('Delete this follow-up? This cannot be undone.')) return;
    try {
      await leadService.deleteReminder(reminderId, user.id);
      setCompleted(prev => prev.filter(r => r.id !== reminderId));
    } catch (err) {
      flash('error', err.message);
    }
  };

  const handleViewProfile = async (r) => {
    if (!r.connection_id) {
      navigate('/app/contacts');
      return;
    }
    try {
      const leads = await leadService.getLeads(user.id);
      const lead = leads.find(l => l.id === r.connection_id);
      if (lead) setSelectedLead(lead);
      else navigate('/app/connections');
    } catch (err) {
      flash('error', err.message);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Spinner /></div>;

  const today = startOfDay(new Date());
  
  const overdue = reminders.filter(r => isPast(startOfDay(new Date(r.due_date))) && !isToday(startOfDay(new Date(r.due_date))));
  const dueToday = reminders.filter(r => isToday(startOfDay(new Date(r.due_date))));
  const upcoming = reminders.filter(r => !isPast(startOfDay(new Date(r.due_date))) && !isToday(startOfDay(new Date(r.due_date))));

  const ReminderSection = ({ title, items, icon, colorClass, emptyMessage }) => {
    if (items.length === 0) {
      return (
        <div className="mb-10">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
            <SafeIcon icon={icon} className={`mr-2 ${colorClass}`} /> {title}
          </h2>
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-6 text-center text-slate-500 text-sm">
            {emptyMessage}
          </div>
        </div>
      );
    }

    return (
      <div className="mb-10">
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
          <SafeIcon icon={icon} className={`mr-2 ${colorClass}`} /> {title} <span className="ml-2 bg-slate-100 text-slate-600 text-xs py-0.5 px-2 rounded-full">{items.length}</span>
        </h2>
        <div className="space-y-4">
          {items.map(r => (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col sm:flex-row justify-between gap-4 group hover:shadow-md transition-shadow">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-slate-900 text-lg flex items-center">
                    {r.card_leads?.full_name || r.contacts?.full_name}
                    {r.card_leads?.is_important && <SafeIcon icon={FiIcons.FiStar} className="ml-1.5 text-amber-500 fill-current text-sm" />}
                  </h3>
                  <span className="text-xs font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                    {r.card_leads?.status?.replace('_', ' ')}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-3">{r.card_leads?.job_title} {r.card_leads?.company_name ? `at ${r.card_leads.company_name}` : ''}</p>
                <div className="flex items-start gap-2">
                  <SafeIcon icon={FiIcons.FiMessageSquare} className="text-slate-400 mt-0.5" />
                  <p className="text-sm font-medium text-slate-800">{r.note || 'No specific note provided.'}</p>
                </div>
                {r.moments?.title && <p className="text-xs text-slate-500 mt-1">From Moment: {r.moments.title}</p>}
                <p className="text-xs text-slate-500 mt-2">Scheduled for: {new Date(r.due_date).toLocaleDateString()}</p>
                {completingId === r.id && (
                  <div className="mt-3">
                    <CompleteNote onCancel={() => setCompletingId(null)} onConfirm={(n) => handleComplete(r.id, n)} />
                  </div>
                )}
              </div>
              <div className="flex sm:flex-col gap-2 justify-center sm:justify-start pt-4 border-t border-slate-100 sm:border-t-0 sm:pt-0">
                <Button variant="primary" size="sm" className="w-full" onClick={() => handleViewProfile(r)}>
                  View Profile
                </Button>
                <Button variant="secondary" size="sm" className="w-full text-green-600 hover:text-green-700 hover:bg-green-50 hover:border-green-200" onClick={() => setCompletingId(r.id)}>
                  <SafeIcon icon={FiIcons.FiCheck} className="mr-1" /> Complete
                </Button>
                <Button variant="secondary" size="sm" className="w-full" onClick={() => setEditing(r)}>
                  <SafeIcon icon={FiIcons.FiEdit2} className="mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="w-full text-slate-400 hover:text-red-500" onClick={() => handleDismiss(r.id)}>
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-4xl pb-12">
      {(creating || editing) && (
        <FollowUpModal
          reminder={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={(row, text) => {
            setCreating(false);
            setEditing(null);
            fetchReminders();
            flash('success', text);
          }}
        />
      )}
      {selectedLead && (
        <ConnectionDetailModal
          lead={selectedLead}
          user={user}
          onClose={() => { setSelectedLead(null); fetchReminders(); }}
          onUpdate={(u) => setSelectedLead(prev => ({ ...prev, ...u }))}
        />
      )}

      <header className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Reminder Centre</h1>
        <p className="mt-2 text-slate-600">Track and manage your upcoming relationship follow-ups.</p>
      </header>

      <div className="mb-8">
        <Button onClick={() => setCreating(true)}>
          <SafeIcon icon={FiIcons.FiPlus} className="mr-2" /> New follow-up
        </Button>
      </div>

      <AskFollowUp
        userId={user.id}
        onSaved={(text, kind) => {
          fetchReminders();
          if (kind === 'watch') setWatchVersion(v => v + 1);
          flash('success', text);
        }}
      />

      {message && <Alert variant={message.variant} message={message.text} className="mb-6" />}

      <OpportunityWatchPanel
        userId={user.id}
        version={watchVersion}
        onFlash={flash}
        onFollowUpSaved={fetchReminders}
      />

      <ReminderSection 
        title="Overdue" 
        items={overdue} 
        icon={FiIcons.FiAlertCircle} 
        colorClass="text-red-500" 
        emptyMessage="Great job! You have no overdue follow-ups." 
      />
      
      <ReminderSection 
        title="Due Today" 
        items={dueToday} 
        icon={FiIcons.FiCalendar} 
        colorClass="text-amber-500" 
        emptyMessage="No follow-ups scheduled for today." 
      />
      
      <ReminderSection 
        title="Upcoming" 
        items={upcoming} 
        icon={FiIcons.FiClock} 
        colorClass="text-blue-500" 
        emptyMessage="No future follow-ups scheduled." 
      />

      <div className="mb-10">
        <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
          <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-2 text-green-500" /> Completed
          {completed.length > 0 && <span className="ml-2 bg-slate-100 text-slate-600 text-xs py-0.5 px-2 rounded-full">{completed.length}</span>}
        </h2>
        {completed.length === 0 ? (
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-6 text-center text-slate-500 text-sm">
            Completed follow-ups will appear here.
          </div>
        ) : (
          <div className="space-y-4">
            {completed.map(r => (
              <div key={r.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex flex-col sm:flex-row justify-between gap-4">
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">{r.card_leads?.full_name || r.contacts?.full_name || 'Unnamed contact'}</h3>
                  <p className="text-sm font-medium text-slate-800 mt-1">{r.note || 'No specific note provided.'}</p>
                  {r.moments?.title && <p className="text-xs text-slate-500 mt-1">From Moment: {r.moments.title}</p>}
                  <p className="text-xs text-slate-500 mt-2">
                    Scheduled for: {new Date(r.due_date).toLocaleDateString()}
                    {r.completed_at ? ` · Completed ${new Date(r.completed_at).toLocaleDateString()}` : ''}
                  </p>
                  {r.completion_note && <p className="text-xs text-slate-600 mt-1">Note: {r.completion_note}</p>}
                </div>
                <div className="flex sm:flex-col gap-2 justify-center sm:justify-start">
                  <Button variant="secondary" size="sm" className="w-full" onClick={() => handleReopen(r.id)}>Reopen</Button>
                  <Button variant="ghost" size="sm" className="w-full text-slate-400 hover:text-red-500" onClick={() => handleDeleteCompleted(r.id)}>Delete</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};